'use client'

import { ModuleEnum } from '@/enums'
import { NotificationType } from '@/types/notification'
import {
  LocaleFormatter,
  BackupsFormatter,
  ProductsExcelExportFormatter,
  ProductsQrExportFormatter,
  ProductsPdfExportFormatter,
  ProductsExcelImportFormatter,
  ProductsPictureImportFormatter,
  ContactFormMessagesFormatter,
  JobApplicationsFormatter,
  OrdersFormatter,
  UsersFormatter
} from '@/lib/formatter/content'

/**
 * Formatted notification item - extends NotificationType with an optional link and extra messages
 */
export type FormattedNotificationItemType = NotificationType & {
  link?: string
  additionalParams?: Array<{
    key: string
    values?: Record<string, any> | undefined
  }>
}

/**
 * Base formatter interface for notifications
 */
export interface INotificationFormatter {
  format(notification: NotificationType): FormattedNotificationItemType
}

/**
 * Default formatter for modules without specific implementation
 * Returns notification as-is without additional properties
 */
const defaultFormatter: INotificationFormatter = {
  format: (notification: NotificationType): FormattedNotificationItemType =>
    notification as FormattedNotificationItemType
}

/**
 * Formatter registry for different notification modules
 * Only specific modules need custom formatters; others use the default
 */
const formatterRegistry: Record<ModuleEnum, INotificationFormatter> = {
  [ModuleEnum.LOCALES]: new LocaleFormatter(),
  [ModuleEnum.BACKUPS]: new BackupsFormatter(),
  [ModuleEnum.PRODUCTS_EXCEL_EXPORTS]: new ProductsExcelExportFormatter(),
  [ModuleEnum.PRODUCTS_QR_EXPORTS]: new ProductsQrExportFormatter(),
  [ModuleEnum.PRODUCTS_PDF_EXPORTS]: new ProductsPdfExportFormatter(),
  [ModuleEnum.PRODUCTS_EXCEL_IMPORTS]: new ProductsExcelImportFormatter(),
  [ModuleEnum.PRODUCTS_PICTURE_IMPORTS]: new ProductsPictureImportFormatter(),
  [ModuleEnum.CONTACT_FORM_MESSAGES]: new ContactFormMessagesFormatter(),
  [ModuleEnum.JOB_APPLICATIONS]: new JobApplicationsFormatter(),
  [ModuleEnum.ORDERS]: new OrdersFormatter(),
  [ModuleEnum.USERS]: new UsersFormatter()
} as Record<ModuleEnum, INotificationFormatter>

/**
 * Format a notification using the appropriate formatter for its module
 * Falls back to default formatter if no specific formatter is registered
 * @param notification The notification to format
 * @returns Formatted notification item
 */
export const formatNotification = (notification: NotificationType): FormattedNotificationItemType => {
  const formatter = formatterRegistry[notification.module as ModuleEnum] || defaultFormatter

  return formatter.format(notification)
}
